/* eslint-disable @typescript-eslint/camelcase */
import i18next from 'i18next';
import * as Sentry from '@sentry/browser';

import * as types from 'constants/CAFA';
import * as api from 'services/CAFA';
import { getSelectedPos, getSelectedPosID } from 'reducers/PointsOfSale';
import {
  getActiveIntegrationsConfig,
  getCafaEntry,
  getIsLoadingCafa,
} from 'reducers/cafaConfigs';
import { getUserLoggedIn } from 'reducers/Login';
import { REDUX_APP_NAME } from 'constants/persistence';
import { waitForCondition } from 'utils';
import {
  CAFA_CONFIGS_COMPANY,
  CAFA_CONFIGS_POS,
  CAFA_CONFIGS_USER,
  CAFA_CONFIGS_WAREHOUSE,
} from 'containers/EssentialDataLoader/constants';
import {
  withIndexedDbPersister,
  invalidateEssentialData,
} from 'containers/EssentialDataLoader/utils';

import { addError, addWarning, dismissType } from '../Error';

export function makeActions(type) {
  return {
    start: () => ({ type: type.START }),
    success: payload => ({ type: type.SUCCESS, payload }),
    error: payload => ({ type: type.ERROR, payload }),
  };
}

export const saveCafaConfigAction = makeActions(types.SAVE_CAFA_CONFIGS);

function getIdbKey(level) {
  switch (level) {
    case types.CAFA_LEVELS.User:
      return CAFA_CONFIGS_USER;
    case types.CAFA_LEVELS.Pos:
      return CAFA_CONFIGS_POS;
    case types.CAFA_LEVELS.Warehouse:
      return CAFA_CONFIGS_WAREHOUSE;
    case types.CAFA_LEVELS.Company:
    default:
      return CAFA_CONFIGS_COMPANY;
  }
}

function fetchCafaConfigPerLevel({ level, level_id }, callerOptions) {
  const idbKey = getIdbKey(level);
  const thunk = withIndexedDbPersister(
    idbKey,
    async ({ level, level_id }) => {
      const { data } = await api.getBulkConfigurations({
        level,
        level_id,
      });

      const newData = {};
      data.forEach(c => {
        newData[c.id] = c;
      });
      return newData;
    },
    {
      provideLevel: (_state, args) => ({
        level: args.level,
        id: String(args.level_id),
      }),
    },
  );

  return thunk({ level, level_id }, callerOptions);
}

/**
 * Fetches all configurations from CAFA related to the current POS, Warehouse and Company it belongs to
 */

export function fetchAllRelatedCafaConfigs() {
  return async (dispatch, getState) => {
    dispatch({ type: types.FETCH_CAFA_CONFIGS.START });

    const selectedPos = getSelectedPos(getState());
    const { userID } = getUserLoggedIn(getState());
    if (!selectedPos) {
      const message =
        'Error getting current POS - this can happen when logging out too soon after just logging in';
      console.error(message);
      dispatch({ type: types.FETCH_CAFA_CONFIGS.ERROR, payload: message });
      throw new Error(message);
    }

    const { warehouseID, pointOfSaleID } = selectedPos;
    const promises = [
      fetchCafaConfigPerLevel({
        level: types.CAFA_LEVELS.Pos,
        level_id: pointOfSaleID,
      }),
      fetchCafaConfigPerLevel({
        level: types.CAFA_LEVELS.User,
        level_id: userID,
      }),
      fetchCafaConfigPerLevel({
        level: types.CAFA_LEVELS.Warehouse,
        level_id: warehouseID,
      }),
      fetchCafaConfigPerLevel({
        level: types.CAFA_LEVELS.Company,
        level_id: '0',
      }),
    ].map(dispatch);

    const configs = await Promise.allSettled(promises);

    // get rejected promises
    const failedConfigs = configs.filter(
      config => config.status === 'rejected',
    );

    if (failedConfigs.length) {
      const error = new Error('Failed to fetch some configurations from CAFA', {
        errors: failedConfigs.map(config => config.reason),
      });
      Sentry.captureException(error);
      throw error;
    }

    const result = configs
      .filter(config => config.status === 'fulfilled')
      .reduce((all, current) => ({ ...all, ...current.value }), {});

    dispatch({ type: types.FETCH_CAFA_CONFIGS.SUCCESS, payload: result });
    return result;
  };
}

/**
 * Delete CAFA configuration
 * @param integrationName - integration name
 * @param integrationType - integration type
 */
export function deleteCAFAConfig({ integrationName, integrationType }) {
  return async (dispatch, getState) => {
    const state = getState();
    const integration = getCafaEntry(integrationName, integrationType)(state);

    // If integration does not exist, abort deletion request
    if (!integration.name) {
      return dispatch(addError('Integration configuration does not exist'));
    }

    const { id, level, name, type, level_id } = integration;
    // Only allow deletion of integrations configured for the current POS
    if (![types.CAFA_LEVELS.Pos, types.CAFA_LEVELS.User].includes(level)) {
      return dispatch(
        addError(
          'Integration configuration is inherited and can not be deleted.',
        ),
      );
    }
    dispatch({ type: types.DELETE_CAFA_CONFIG.START });
    try {
      await api.deleteConfiguration(id);
      dispatch({
        type: types.DELETE_CAFA_CONFIG.SUCCESS,
        payload: integration,
      });
      invalidateEssentialData(item => item.key === getIdbKey(level));
    } catch (err) {
      console.error(
        'Unable to delete cafa record',
        { level, name, type, level_id },
        err,
      );
      dispatch({ type: types.DELETE_CAFA_CONFIG.ERROR, payload: err.message });
    }
    return true;
  };
}

/**
 * Save CAFA configuration
 *
 * If configuration with such name already exists , updateCafaConfig will be dispatched
 * @param params {object}
 * @param params.integrationName {string}
 * @param params.integrationType {string}
 * @param [params.level] {undefined | {level: 'Warehouse'|'Company'|'Pos'|'User', id: string|undefined}}
 * @param params.config {object | string | boolean}
 */
export function saveCafaConfig({
  integrationName,
  integrationType = '',
  level = undefined,
  config,
}) {
  return async (dispatch, getState) => {
    const state = getState();
    const posID = getSelectedPosID(state);

    const searchLevel = {
      id: level ? level.id : posID,
      level: level ? level.level : types.CAFA_LEVELS.Pos,
    };
    const integration = getCafaEntry(
      integrationName,
      integrationType,
      searchLevel,
    )(state);

    dispatch(saveCafaConfigAction.start());
    try {
      const params = {
        ...integration,
        application: REDUX_APP_NAME,
        level: searchLevel.level,
        level_id: searchLevel.id,
        type: integrationType,
        name: integrationName,
        value: config,
      };
      if (!params.value) {
        if (params?.id) {
          await dispatch(
            deleteCAFAConfig({ integrationName, integrationType }),
          );
        } else {
          console.warn('Cannot save empty configuration to CAFA.');
          return;
        }
      } else if (params?.id) {
        await api.updateConfiguration(params);
      } else {
        const response = await api.saveConfiguration(params);
        params.id = response?.data?.id;
      }
      dispatch(saveCafaConfigAction.success(params));
      invalidateEssentialData(
        item =>
          item.key === getIdbKey(level ? level.level : types.CAFA_LEVELS.Pos),
      );
    } catch (err) {
      dispatch(saveCafaConfigAction.error(err.message));
      throw new Error(`Failed to save cafa configuration`, { cause: err });
    }
  };
}

/**
 * Save selected integration as default for the selected integration type
 * @param {INTEGRATION_TYPES} integrationType - type of the integration
 * @param {string} value - selected payment integration, example: 'adyen'
 */
export function saveActiveIntegration({ integrationType, value }) {
  return async (dispatch, getState) => {
    const state = getState();
    const posID = getSelectedPosID(getState(state));
    const activeIntegrationsConfig = getActiveIntegrationsConfig(state);

    dispatch(saveCafaConfigAction.start());

    const newActiveIntegrationConfig = {
      ...activeIntegrationsConfig,
      value: {
        ...activeIntegrationsConfig?.value,
        [integrationType]: value,
      },
      type: types.INTEGRATION_TYPES.integrations,
      name: types.INTEGRATIONS[types.INTEGRATION_TYPES.integrations].active,
      level: types.CAFA_LEVELS.Pos,
      level_id: posID,
    };

    try {
      if (
        activeIntegrationsConfig?.id &&
        activeIntegrationsConfig?.level === types.CAFA_LEVELS.Pos
      ) {
        await api.updateConfiguration(newActiveIntegrationConfig);
      } else {
        await api.saveConfiguration(newActiveIntegrationConfig);
      }
      dispatch(saveCafaConfigAction.success(newActiveIntegrationConfig));
      invalidateEssentialData(item => item.key === CAFA_CONFIGS_POS);
    } catch (err) {
      console.error('Failed to save active integration to CAFA', err);
      dispatch(saveCafaConfigAction.error(err.message));
    }
  };
}

export function waitForCafaToLoad() {
  return async (dispatch, getState) => {
    const s = 1000;
    const testFn = () => !getIsLoadingCafa(getState());
    const callback = () =>
      dispatch(
        addWarning(i18next.t('alerts:loading.cafa'), {
          selfDismiss: false,
          errorType: 'cafa-waiting',
        }),
      );

    try {
      await waitForCondition({
        testFn,
        timeout: 60 * s,
        stability: 0.75 * s,
        checkInterval: 0.1 * s,
        callbacks: {
          [1 * s]: callback,
        },
      });
    } catch (error) {
      dispatch(
        addError(i18next.t('alerts:loading.cafa_error'), {
          selfDismiss: 5000,
          dismissible: true,
        }),
      );
      throw error;
    } finally {
      dispatch(dismissType('cafa-waiting'));
    }
  };
}
